/**
 * Namespace flattening for the `@richrich/i18n` catalog.
 *
 * A catalog is authored as one file per namespace (`nav`, `common`, …), each a
 * flat object of dot-free local keys. This helper merges those namespaces into a
 * single flat lookup map whose keys are namespace-prefixed (`nav.dashboard`).
 *
 * CONTRACT: the runtime merge and the `Prefixed<>` type are kept in lockstep —
 * the value type is intentionally widened to `string` (message *values* are never
 * used at the type level; only the *key set* is load-bearing, since it defines
 * `MessageKey`). Local keys must contain no `.` so a prefixed key round-trips to
 * exactly one `<namespace>.<localKey>` pair.
 */

/** A single namespace: dot-free local keys → message strings. */
export type Namespace = Readonly<Record<string, string>>;

/** A map of namespace name → namespace. */
export type NamespaceMap = Readonly<Record<string, Namespace>>;

/** The namespace-prefixed key set produced by {@link flatten}. */
export type Prefixed<NS extends NamespaceMap> = {
  [N in keyof NS & string as `${N}.${Extract<keyof NS[N], string>}`]: string;
};

/**
 * Merge namespaces into a single flat map with `<namespace>.<key>` keys. Pure;
 * no external dependencies. The cast is sound because the loop reproduces exactly
 * the key shape encoded by {@link Prefixed}.
 */
export function flatten<NS extends NamespaceMap>(namespaces: NS): Prefixed<NS> {
  const out: Record<string, string> = {};
  for (const [ns, entries] of Object.entries(namespaces)) {
    for (const [key, value] of Object.entries(entries)) {
      out[`${ns}.${key}`] = value;
    }
  }
  return out as Prefixed<NS>;
}
